import { AutomaticRecoveryProgress, RecoveryFieldChanges } from "./automatic-value-recovery"
import {
  PREVIEW_FRESHNESS,
  PRODUCT_VALUE_RECOVERY_KIND,
  type ProductValueRecoveryPlan,
  type ProductValueRecoveryReview,
  type RunFeedPreviewFreshness,
} from "@nova-ai/shared"

import { humanizeEnumLabel } from "../../lib/enum-label"
import { humanizeMarketplaceId } from "../../lib/marketplace"
import { useProductValueRecovery } from "../../services/use-product-value-recovery"
import { Button } from "../ui/button"
import { Table } from "../ui/table"

const PLAN_LABELS = {
  [PRODUCT_VALUE_RECOVERY_KIND.replaying]: "Replaying current changes",
  [PRODUCT_VALUE_RECOVERY_KIND.candidateBuilding]: "Building current values",
  [PRODUCT_VALUE_RECOVERY_KIND.buildRequired]: "A fresh build is needed",
  [PRODUCT_VALUE_RECOVERY_KIND.reviewable]: "Review marketplace changes",
  [PRODUCT_VALUE_RECOVERY_KIND.blocked]: "Value recovery is blocked",
}

export function ProductValueRecoveryPanel({
  marketplace,
  originatingRunId,
  slug,
  runProductCount,
  freshness,
}: {
  marketplace: string
  originatingRunId: number
  slug: string
  runProductCount: number
  freshness: RunFeedPreviewFreshness | undefined
}) {
  const recovery = useProductValueRecovery(marketplace, originatingRunId)
  const { data: plan } = recovery.plan
  const changesGenerationId = recoveryChangesGeneration(plan)
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-[15px] font-semibold">
          {humanizeMarketplaceId(marketplace)} value recovery
        </h2>

        <p className="mt-1 max-w-prose text-muted-foreground">
          This changes values for the entire marketplace. Every run uses these values. No feed is
          approved or uploaded.
        </p>
      </div>

      <RecoveryRunScope
        slug={slug}
        runProductCount={plan?.originatingRun?.products ?? runProductCount}
        freshness={freshness}
      />

      {plan?.originatingRun && (
        <p>
          {(plan.originatingRun.affectedProducts ?? "Unknown number of").toLocaleString()} affected
          products across the full originating run.
        </p>
      )}

      {recovery.plan.isPending && <p role="status">Loading the current recovery plan…</p>}

      {recovery.plan.error && (
        <p role="alert">
          Recovery plan unavailable: {recovery.plan.error.message} Activation is disabled.
        </p>
      )}

      <AutomaticRecoveryProgress marketplace={marketplace} runId={originatingRunId} />

      {plan && <RecoveryStatus plan={plan} isComplete={recovery.isComplete} />}

      {plan?.review && <RecoveryImpact review={plan.review} />}

      {changesGenerationId && (
        <RecoveryFieldChanges
          key={changesGenerationId}
          marketplace={marketplace}
          generationId={changesGenerationId}
        />
      )}

      <RecoveryActions recovery={recovery} />
    </div>
  )
}

function RecoveryRunScope({
  slug,
  runProductCount,
  freshness,
}: {
  slug: string
  runProductCount: number
  freshness: RunFeedPreviewFreshness | undefined
}) {
  const unsafe = freshness?.kind === PREVIEW_FRESHNESS.historical ? freshness.unsafeProducts : null
  const sample = unsafe?.items
    .slice(0, 5)
    .map((item) => item.configSku)
    .join(", ")
  const sampleText = sample ? ` Sample: ${sample}.` : ""
  const remainder = unsafe?.truncated ? " More affected products are outside this sample." : ""
  return (
    <div className="space-y-1">
      <h3 className="font-medium">Originating run: {slug}</h3>

      <p>{runProductCount.toLocaleString()} products in this run.</p>

      {unsafe && (
        <p>
          {`${unsafe.total.toLocaleString()} affected products on this loaded view.${sampleText}${remainder}`}
        </p>
      )}
    </div>
  )
}

function RecoveryStatus({
  plan,
  isComplete,
}: {
  plan: ProductValueRecoveryPlan
  isComplete: boolean
}) {
  return (
    <div className="space-y-2" role="status" aria-live="polite">
      <h3 className="font-medium">
        {isComplete ? "Marketplace values are current" : PLAN_LABELS[plan.kind]}
      </h3>

      {isComplete ? (
        <p>Marketplace values have converged. See the separate whole-run check outcome above.</p>
      ) : (
        <>
          {plan.reasons.map((reason) => (
            <p key={`${reason.code}:${reason.message}`}>{reason.message}</p>
          ))}

          <p>
            {`${plan.replay.pending.toLocaleString()} queued updates pending replay. ${plan.replay.uniqueProducts ?? "Unknown number of"} unique products. Several updates can affect one product.`}
          </p>

          {plan.candidate && (
            <p>
              {`${humanizeEnumLabel(plan.candidate.status)}.`}

              {` ${plan.candidate.completedProducts.toLocaleString()} of ${plan.candidate.totalProducts.toLocaleString()} products processed; ${plan.candidate.technicalFailures.toLocaleString()} technical failures.`}
            </p>
          )}
        </>
      )}
    </div>
  )
}

function RecoveryImpact({ review }: { review: ProductValueRecoveryReview }) {
  const impact = review.impact
  const rollbackSummary = review.rollback.available
    ? "Rollback is available."
    : "This activation cannot be rolled back."
  return (
    <div className="space-y-3">
      <h3 className="font-medium">Full marketplace impact</h3>

      <p>
        {`${impact.added.toLocaleString()} added · ${impact.removed.toLocaleString()} removed · ${impact.changed.toLocaleString()} changed · ${impact.unchanged.toLocaleString()} unchanged`}
      </p>

      <Table
        embedded
        dense
        rows={impact.sample}
        rowKey={(row) => row.configSku}
        columns={[
          { key: "sku", header: "Product sample", render: (row) => row.configSku },
          { key: "change", header: "Change", render: (row) => humanizeEnumLabel(row.change) },
        ]}
        empty="No changed products in this sample."
      />

      {impact.truncated && (
        <p className="text-muted-foreground">
          This sample does not include every affected product.
        </p>
      )}

      <p className="font-medium">{`${rollbackSummary} ${review.rollback.message}`}</p>

      <details className="break-words">
        <summary className="cursor-pointer text-muted-foreground">
          Technical review evidence
        </summary>

        <dl className="mt-2 space-y-2">
          <div>
            <dt className="font-medium">Generation and transition</dt>

            <dd>{`${review.generationId} · ${humanizeEnumLabel(review.intent)}`}</dd>
          </div>

          <div>
            <dt className="font-medium">Replay range</dt>

            <dd>
              {`${review.replayRange.fromEventId} to ${review.replayRange.toEventId}; ${review.replayRange.pending} pending`}
            </dd>
          </div>

          <div>
            <dt className="font-medium">Source watermark</dt>

            <dd>
              {`Event ${review.watermark?.eventId ?? "unknown"}; ${review.watermark?.sourceUpdatedAt ? new Date(review.watermark.sourceUpdatedAt).toLocaleString() : "source time unknown"}`}
            </dd>
          </div>

          {Object.entries(review.identities).map(([key, value]) => (
            <div key={key}>
              <dt className="font-medium">{humanizeEnumLabel(key)}</dt>

              <dd className="break-all font-mono text-xs">{value ?? "Unknown"}</dd>
            </div>
          ))}
        </dl>
      </details>
    </div>
  )
}

function RecoveryActions({ recovery }: { recovery: ReturnType<typeof useProductValueRecovery> }) {
  const error = recovery.activate.error ?? recovery.build.error
  return (
    <div className="space-y-3">
      {error && <p role="alert">{error.message} Review the fresh plan before trying again.</p>}

      {recovery.activate.isSuccess && !recovery.isComplete && (
        <p role="status">
          Activation accepted. Recovery is still in progress until replay finishes.
        </p>
      )}

      <RecoveryConfirmation recovery={recovery} />

      <RecoveryButtons recovery={recovery} />
    </div>
  )
}

function RecoveryConfirmation({
  recovery,
}: {
  recovery: ReturnType<typeof useProductValueRecovery>
}) {
  const review = recovery.plan.data?.review
  if (!review || review.rollback.available) {
    return null
  }
  return (
    <label className="flex items-start gap-2">
      <input
        type="checkbox"
        className="mt-1 size-4 accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
        checked={recovery.isConfirmed}
        disabled={!recovery.canAct}
        onChange={(event) => recovery.confirm(event.target.checked)}
      />

      <span>I understand this marketplace-wide activation cannot be rolled back.</span>
    </label>
  )
}

function RecoveryButtons({ recovery }: { recovery: ReturnType<typeof useProductValueRecovery> }) {
  const plan = recovery.plan.data
  const review = plan?.review
  const isBusy = recovery.plan.isFetching || recovery.build.isPending || recovery.activate.isPending
  return (
    <div className="flex flex-wrap gap-2">
      {plan?.kind === PRODUCT_VALUE_RECOVERY_KIND.buildRequired && (
        <Button
          variant="primary"
          disabled={!recovery.canAct}
          onClick={() => recovery.build.mutate()}
        >
          {recovery.build.isPending ? "Starting build…" : "Build current values"}
        </Button>
      )}

      {review && (
        <Button
          variant="primary"
          disabled={!recovery.canAct || (!review.rollback.available && !recovery.isConfirmed)}
          onClick={() => recovery.activate.mutate()}
        >
          {recovery.activate.isPending ? "Activating…" : "Activate reviewed values"}
        </Button>
      )}

      {!recovery.isComplete && (
        <Button disabled={isBusy} onClick={() => void recovery.plan.refetch()}>
          {recovery.plan.isFetching ? "Refreshing plan…" : "Refresh recovery plan"}
        </Button>
      )}
    </div>
  )
}

function recoveryChangesGeneration(plan: ProductValueRecoveryPlan | undefined) {
  return plan?.candidate?.status === "ready"
    ? plan.candidate.generationId
    : plan?.activeGenerationId
}
